'use client';

import { useParams } from 'next/navigation';
import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { useSession } from 'next-auth/react';

interface Farm {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

interface FarmContextValue {
  selectedFarm: Farm | null;
  availableFarms: Farm[];
  loading: boolean;
  selectFarm: (farmId: string | null) => void;
  isAdmin: boolean;
  refetch: () => Promise<void>;
}

const FarmContext = createContext<FarmContextValue | undefined>(undefined);

interface FarmContextProviderProps {
  children: ReactNode;
}

const SELECTED_FARM_KEY = 'selected_farm_id';
const FARMS_CACHE_KEY = 'user_farms_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

interface CachedFarms {
  farms: Farm[];
  timestamp: number;
  userId: string;
}

/**
 * Provider that manages farm selection and availability
 *
 * Features:
 * - Manages selected farm globally
 * - Caches available farms in memory and localStorage
 * - Persists selected farm in localStorage
 * - Automatically loads user's accessible farms
 * - Admin users see all farms
 * - Regular users only see their assigned farms
 */
export function FarmContextProvider({ children }: FarmContextProviderProps) {
  const params = useParams<{ tenant: string }>();
  const tenant = params?.tenant;

  const { data: session, status } = useSession();
  const [selectedFarm, setSelectedFarm] = useState<Farm | null>(null);
  const [availableFarms, setAvailableFarms] = useState<Farm[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Load available farms from API or cache
  const loadFarms = useCallback(async (skipCache = false) => {
    if (status === 'loading') {
      return;
    }

    // ✅ GUARD: Don't load farms if tenant is undefined (e.g., on /select-tenant route)
    if (!tenant) {
      setAvailableFarms([]);
      setSelectedFarm(null);
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    if (!session?.user?.id) {
      setAvailableFarms([]);
      setSelectedFarm(null);
      setIsAdmin(false);
      setLoading(false);
      // Clear cache when user logs out
      if (typeof window !== 'undefined') {
        localStorage.removeItem(FARMS_CACHE_KEY);
        localStorage.removeItem(SELECTED_FARM_KEY);
      }
      return;
    }

    try {
      // Check cache first (if not forcing refresh)
      if (!skipCache && typeof window !== 'undefined') {
        const cachedData = localStorage.getItem(FARMS_CACHE_KEY);
        if (cachedData) {
          try {
            const parsed: CachedFarms = JSON.parse(cachedData);
            const now = Date.now();
            const isValid =
              parsed.userId === session.user.id &&
              parsed.timestamp &&
              (now - parsed.timestamp) < CACHE_DURATION;

            if (isValid && parsed.farms) {
              // Use cached farms
              setAvailableFarms(parsed.farms);

              // Restore selected farm if it exists in available farms
              const savedFarmId = localStorage.getItem(SELECTED_FARM_KEY);
              if (savedFarmId) {
                const farm = parsed.farms.find(f => f.id === savedFarmId);
                if (farm) {
                  setSelectedFarm(farm);
                } else {
                  // Saved farm not in available farms, select first one
                  setSelectedFarm(parsed.farms[0] || null);
                }
              } else {
                // No saved farm, select first one
                setSelectedFarm(parsed.farms[0] || null);
              }

              setLoading(false);
              return;
            }
          } catch (parseError) {
            // Invalid cache, remove it
            localStorage.removeItem(FARMS_CACHE_KEY);
          }
        }
      }

      // Fetch from API
      // First, check if user is admin
      const meResponse = await fetch('/api/auth/me');
      if (meResponse.ok) {
        const meData = await meResponse.json();
        const adminRole = meData.user?.userRoles?.some(
          // Same rule as lib/permissions/farmAccess.ts: these roles see every farm of the tenant.
          (ur: any) => ['admin', 'tenant_owner'].includes(ur.role.name)
        );
        setIsAdmin(adminRole);

        // Get farms based on user type
        let farmsData: Farm[] = [];

        if (adminRole) {
          // Admin: get all farms
          const farmsResponse = await fetch(`/api/${tenant}/farms?page=1&pageSize=1000`);
          if (farmsResponse.ok) {
            const data = await farmsResponse.json();
            farmsData = data.data || []; // La API devuelve 'data' no 'farms'
          }
        } else {
          // Regular user: get assigned farms
          const userFarmsResponse = await fetch(`/api/${tenant}/users/${session.user.id}/farms`);
          if (userFarmsResponse.ok) {
            const data = await userFarmsResponse.json();
            farmsData = data.farms || [];
          }
        }

        setAvailableFarms(farmsData);

        // Cache the farms
        if (typeof window !== 'undefined') {
          const cacheData: CachedFarms = {
            farms: farmsData,
            timestamp: Date.now(),
            userId: session.user.id,
          };
          localStorage.setItem(FARMS_CACHE_KEY, JSON.stringify(cacheData));
        }

        // Restore or select first farm
        if (typeof window !== 'undefined') {
          const savedFarmId = localStorage.getItem(SELECTED_FARM_KEY);
          if (savedFarmId) {
            const farm = farmsData.find(f => f.id === savedFarmId);
            if (farm) {
              setSelectedFarm(farm);
            } else {
              // Saved farm not in available farms, select first one
              const firstFarm = farmsData[0] || null;
              setSelectedFarm(firstFarm);
              if (firstFarm) {
                localStorage.setItem(SELECTED_FARM_KEY, firstFarm.id);
              }
            }
          } else {
            // No saved farm, select first one
            const firstFarm = farmsData[0] || null;
            setSelectedFarm(firstFarm);
            if (firstFarm) {
              localStorage.setItem(SELECTED_FARM_KEY, firstFarm.id);
            }
          }
        } else {
          setSelectedFarm(farmsData[0] || null);
        }
      } else {
        setAvailableFarms([]);
        setSelectedFarm(null);
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Error loading farms:', error);
      setAvailableFarms([]);
      setSelectedFarm(null);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [session?.user?.id, status, tenant]);

  // Load farms on mount and when session changes
  useEffect(() => {
    loadFarms();
  }, [loadFarms]);

  // Select a farm by ID
  const selectFarm = useCallback((farmId: string | null) => {
    if (!farmId) {
      setSelectedFarm(null);
      if (typeof window !== 'undefined') {
        localStorage.removeItem(SELECTED_FARM_KEY);
      }
      return;
    }

    const farm = availableFarms.find(f => f.id === farmId);
    if (farm) {
      setSelectedFarm(farm);
      if (typeof window !== 'undefined') {
        localStorage.setItem(SELECTED_FARM_KEY, farmId);
      }
    }
  }, [availableFarms]);

  // Refetch function to manually refresh farms
  const refetch = useCallback(async () => {
    setLoading(true);
    await loadFarms(true); // Skip cache
  }, [loadFarms]);

  const value: FarmContextValue = {
    selectedFarm,
    availableFarms,
    loading,
    selectFarm,
    isAdmin,
    refetch,
  };

  return (
    <FarmContext.Provider value={value}>
      {children}
    </FarmContext.Provider>
  );
}

/**
 * Hook to access farm context
 *
 * @throws Error if used outside of FarmContextProvider
 *
 * @example
 * const { selectedFarm, availableFarms, selectFarm } = useFarmContext();
 *
 * // Get current selected farm
 * if (selectedFarm) {
 *   console.log('Selected farm:', selectedFarm.name);
 * }
 *
 * // Change selected farm
 * selectFarm(newFarmId);
 */
export function useFarmContext() {
  const context = useContext(FarmContext);

  if (context === undefined) {
    throw new Error('useFarmContext must be used within a FarmContextProvider');
  }

  return context;
}
